import type { Editor } from "@tiptap/core";
import { insertBlocks, insertPlaceholder } from "./commands";
import { insertImages, pickImageFile } from "./images";

export type SlashItem = {
  id: string;
  label: string;
  hint: string;
  keywords: string[];
  available: (editor: Editor) => boolean;
  run: (editor: Editor) => boolean | void | Promise<void>;
};

const topLevelText = (e: Editor) => {
  const { $from } = e.state.selection;
  return $from.depth === 1 && $from.parent.type.name === "paragraph";
};
const inCellOrItem = (e: Editor) => {
  const { $from } = e.state.selection;
  return (
    $from.parent.type.name === "paragraph" &&
    $from.depth >= 2 &&
    ["klTableCell", "klGroupItem"].includes($from.node(-1).type.name)
  );
};
const inlineAllowed = (e: Editor) => {
  const p = e.state.selection.$from.parent;
  return p.inlineContent && p.type.name !== "heading" && !p.type.spec.code;
};

export const SLASH_ITEMS: SlashItem[] = [
  {
    id: "text",
    label: "Text",
    hint: "Plain paragraph",
    keywords: ["paragraph", "p"],
    available: topLevelText,
    run: (e) => e.chain().focus().setNode("paragraph").run(),
  },
  {
    id: "h1",
    label: "Heading 1",
    hint: "Numbered section",
    keywords: ["h1", "title", "section"],
    available: topLevelText,
    run: (e) => e.chain().focus().setNode("heading", { level: 1, number: true }).run(),
  },
  {
    id: "h2",
    label: "Heading 2",
    hint: "Numbered subsection",
    keywords: ["h2", "subsection"],
    available: topLevelText,
    run: (e) => e.chain().focus().setNode("heading", { level: 2, number: true }).run(),
  },
  {
    id: "h2plain",
    label: "Heading 2, unnumbered",
    hint: "Subsection without a number",
    keywords: ["h2", "unnumbered"],
    available: topLevelText,
    run: (e) => e.chain().focus().setNode("heading", { level: 2, number: false }).run(),
  },
  {
    id: "bullets",
    label: "Bullets",
    hint: "Bulleted list",
    keywords: ["list", "ul", "bullets"],
    available: topLevelText,
    run: (e) => e.chain().focus().toggleList("bulletList", "listItem").run(),
  },
  {
    id: "steps",
    label: "Steps",
    hint: "Numbered steps",
    keywords: ["list", "ol", "numbered", "steps"],
    available: topLevelText,
    run: (e) => e.chain().focus().toggleList("orderedList", "listItem").run(),
  },
  {
    id: "table",
    label: "Table",
    hint: "Header row and columns",
    keywords: ["table", "grid"],
    available: topLevelText,
    run: (e) => insertBlocks(e, [{ type: "table", columns: ["Column 1", "Column 2"], rows: [["", ""]] }]),
  },
  {
    id: "groups",
    label: "Group list",
    hint: "Groups with a title and items",
    keywords: ["group", "grouptable"],
    available: topLevelText,
    run: (e) => insertBlocks(e, [{ type: "groupTable", groups: [{ title: "Group", items: [""] }] }]),
  },
  {
    id: "kv",
    label: "Key–value",
    hint: "Label and value pairs",
    keywords: ["kv", "key", "value", "pairs"],
    available: topLevelText,
    run: (e) => insertBlocks(e, [{ type: "kv", pairs: [["Key", ""]] }]),
  },
  {
    id: "note",
    label: "Note",
    hint: "Green callout",
    keywords: ["note", "callout", "info"],
    available: topLevelText,
    run: (e) => insertBlocks(e, [{ type: "note", text: "" }]),
  },
  {
    id: "warning",
    label: "Warning",
    hint: "Orange callout",
    keywords: ["warning", "callout", "caution"],
    available: topLevelText,
    run: (e) => insertBlocks(e, [{ type: "warning", text: "" }]),
  },
  {
    id: "code",
    label: "Code",
    hint: "One line of code",
    keywords: ["code", "pre", "shell"],
    available: topLevelText,
    run: (e) => e.chain().focus().setNode("codeBlock").run(),
  },
  {
    id: "codeLine",
    label: "Code line",
    hint: "Code inside a cell or item",
    keywords: ["code"],
    available: inCellOrItem,
    run: (e) => e.chain().focus().setNode("klCodeLine").run(),
  },
  {
    id: "pageBreak",
    label: "Page break",
    hint: "Start a new page in exports",
    keywords: ["page", "break"],
    available: topLevelText,
    run: (e) => insertBlocks(e, [{ type: "pageBreak" }, { type: "p", text: "" }]),
  },
  {
    id: "image",
    label: "Image",
    hint: "PNG, JPEG, WebP or GIF",
    keywords: ["image", "picture", "screenshot"],
    available: topLevelText,
    run: async (e) => {
      const file = await pickImageFile();
      if (file && !e.isDestroyed && e.isEditable) await insertImages(e, [file], null);
    },
  },
  {
    id: "docLink",
    label: "Doc link",
    hint: "Link another document by code",
    keywords: ["link", "doc", "mention", "@"],
    available: inlineAllowed,
    run: (e) => e.chain().focus().insertContent("@").run(),
  },
  {
    id: "placeholder",
    label: "Placeholder",
    hint: "Orange field to fill in later",
    keywords: ["placeholder", "field", "template"],
    available: inlineAllowed,
    run: (e) => insertPlaceholder(e),
  },
];

export function filterSlashItems(query: string, editor: Editor): SlashItem[] {
  const q = query.trim().toLowerCase();
  return SLASH_ITEMS.filter((item) => item.available(editor)).filter(
    (item) => q === "" || item.label.toLowerCase().includes(q) || item.keywords.some((k) => k.startsWith(q)),
  );
}
